import { minorUnitExponent, type SupplierPrice } from "@dentacore/shared-types";

/**
 * How suppliers' numbers are written — STORY-171. One place, so the order
 * dialog, the item panel, the supplier list and Home say a price the same way.
 */

/**
 * A price per use, **with one more decimal than the currency has** — and two
 * more below one minor unit.
 *
 * Per use is where suppliers differ by fractions of a cent: 0.080 against 0.085
 * a glove. Rounded to the currency's own decimals both read "0.08" or "0.09",
 * and the screen would show two equal prices beside a "Cheapest" chip on only
 * one of them. The comparison itself never rounds (`comparePerUse`); this is
 * only how it is shown.
 */
export function perUseText(
  price: { priceCents: number; usesPerPack: number },
  currency: string,
): string {
  const exponent = minorUnitExponent(currency);
  const value = price.priceCents / price.usesPerPack / 10 ** exponent;
  const extra = value < 1 ? 2 : 1;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: exponent + 1,
      maximumFractionDigits: exponent + extra,
    }).format(value);
  } catch {
    return value.toFixed(exponent + extra);
  }
}

/** "2 days to arrive", or nothing when the supplier has not said. */
export function leadText(leadDays: number | null): string | null {
  if (leadDays === null) return null;
  return leadDays === 1 ? "1 day to arrive" : `${String(leadDays)} days to arrive`;
}

/**
 * Their pack, as they name it — "Box of 200". **The number of uses is not
 * repeated after it** (Emile, 2026-10-16: *"if a box has 100 units I know I can
 * use it 100 times"*); it is still what stock counts in, asked for once when the
 * price is entered.
 */
export function packUsesText(price: Pick<SupplierPrice, "pack" | "usesPerPack">): string {
  return price.pack;
}

/** "A and B", "A, B and C". */
export function namesText(names: readonly string[]): string {
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}

/**
 * When an order should arrive: **the day it was sent** plus the supplier's days
 * (SU-D19). Null when it has not been sent — it is not coming yet — or when the
 * supplier gave no days.
 */
export function expectedText(sentAt: string | null, leadDays: number | null): string | null {
  if (sentAt === null || leadDays === null) return null;
  const date = new Date(sentAt);
  date.setDate(date.getDate() + leadDays);
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * The message the assistant sends (SU-D4). DentaCore never sends it; the
 * assistant copies it into whatever they use with that supplier.
 */
export function orderMessage(
  supplierName: string,
  packs: number,
  itemName: string,
  pack: string,
): string {
  return `Hello ${supplierName}, we would like to order ${String(packs)} × ${itemName} (${pack}). Thank you.`;
}

/**
 * The one message for a whole checkout to one supplier (STORY-189c, MOD-D23):
 * every line, then the total. DentaCore never sends it (SU-D4).
 */
export function batchMessage(
  supplierName: string,
  lines: readonly { packs: number; itemName: string; pack: string; priceCents: number }[],
  totalCents: number,
  currency: string,
  money: (cents: number, currency: string) => string,
): string {
  const body = lines
    .map(
      (l) =>
        `- ${String(l.packs)} × ${l.itemName} (${l.pack}) at ${money(l.priceCents, currency)} a pack`,
    )
    .join("\n");
  return `Hello ${supplierName}, we would like to order:\n${body}\nTotal: ${money(totalCents, currency)}. Thank you.`;
}

/** Cells from a sheet arrive as text, numbers, booleans or dates; the server wants text. */
export function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return "";
  if (cell instanceof Date) return cell.toISOString().slice(0, 10);
  const text = String(cell).trim();
  // A float stored as 5.500000000000001 is a spreadsheet's arithmetic, not a price.
  if (/^-?\d+\.\d{7,}$/.test(text)) return String(Number(Number(text).toFixed(6)));
  return text;
}

/**
 * A typed amount in minor units, or null when it is not one.
 *
 * **Refuses more decimals than the currency has** rather than rounding: 24.555
 * dollars is a typo, and rounding it would save a price nobody typed. Built from
 * the digits, never by multiplying a float.
 */
export function typedMinorUnits(typed: string, currency: string): number | null {
  const exponent = minorUnitExponent(currency);
  const text = typed.trim();
  const shape = exponent === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${String(exponent)}})?$`);
  if (!shape.test(text)) return null;
  const [whole = "0", fraction = ""] = text.split(".");
  const minor =
    Number(whole) * 10 ** exponent +
    Number((fraction + "0".repeat(exponent)).slice(0, exponent) || "0");
  return minor > 0 ? minor : null;
}
